<template>
	<div id="app" class="app column flexible" v-handle-tab v-os-theme="$root.isTek" :class="{'touched':$root.pointer === 'touch', 'hoverable':$root.pointer === 'mouse'}">
		<div v-if="showLegacyBanner" class="row notice rigid">
			<i class="fas fa-exclamation-triangle"></i>&nbsp; Account Alert: Your legacy license is scheduled for end of life.&nbsp;<router-link to="/legacy-tekscope" target="_blank">Learn more <i class="fas fa-arrow-right small"></i></router-link>
		</div>
		<header class="page-header row rigid">
			<a v-if="referrerClientUrl" class="return link" :href="getAppUrl" title="Go Back to Previous Page"> <i class="fa fa-arrow-left"></i></a>

			<span v-if="$root.isTek" class="flexible row">
				<dropdown-menu :min-width="172">
					<div slot="element-trigger" class="launch">
						<i class="rigid app-icon"></i>
					</div>
					<a v-if="hasDriveClaims" target="_blank" :href="driveUrl">TekDrive</a>
					<a v-if="hasScopeClaims" target="_blank" :href="scopeUrl">TekScope</a>
				</dropdown-menu>
				<router-link v-if="$root.isTek" to="/" class="rigid product-name">TekCloud Account</router-link>
			</span>
			<a v-else class="launch rigid" :href="iotAppUrl">
				<i class="app-icon"></i>
			</a>

			<span class="flexible row centering"></span>

			<menu>
				<dropdown-menu v-if="userEmail" :position="'right'" :menu-text="userEmail" :icon-class="'fa-user'">
					<span v-if="referrerClientUrl">
						<a href="#/" title="My Account">My Account</a>
						<a :href="appSettingsUrl" title="My Settings">My Settings</a>
						<a v-show="user.roles && user.roles.trigger && user.roles.trigger.list" :href="appContactsUrl" title="My Contacts">My Contacts</a>
						<div class="divider"></div>
					</span>

					<a v-if="!$root.isTek" target="_blank" href="http://support.initialstate.com/hc/en-us/requests/new?feedback=true" class="is-support" title="Send us your feedback">Feedback</a>
					<a v-if="!$root.isTek" href="http://support.initialstate.com/" class="is-support" target="_blank" title="Get assistance and follow changes">Support</a>
					<router-link v-if="$root.isTek" to="/">My Account</router-link>
					<router-link class="sign-out" to="/signout">Sign out</router-link>
				</dropdown-menu>
				<a v-else class="rigid centered" :href="signInUrl">Sign In</a>
			</menu>
		</header>


		<main v-if="pageNotFound">
			<router-view/>
		</main>

		<main v-else-if="confirmEmailChange">
			<router-view/>
		</main>

		<main v-else-if="receipt">
			<router-view/>
		</main>

		<main class="column gap" :class="{'no-scroll':noScroll}" v-else>
			<!-- <transition name="collapse"> -->
			<div class="row">
				<header v-show="!hideMainHeader" class="rigid" :class="{'no-header':hideMainHeader}">
					<menu class="rigid rigid tabs column">
						<router-link class="tab row centering" v-bind:class="{active: $route.name==='home'}" tag="button" to="/">
							<span class="rigid icon-wrapper"><i class="fas fa-home"></i></span>
							<span class="rigid">Home</span>
						</router-link>

						<router-link v-if="$root.isTek" class="tab row centering" v-bind:class="{active: $route.name==='security'}" tag="button" to="/security">
							<span class="rigid icon-wrapper"><i class="fas fa-lock"></i></span>
							<span class="rigid">Security</span>
							<i v-if="showValidationWarning" class="rigid warning fas fa-exclamation-triangle"></i>
						</router-link>
						<router-link v-else class="tab row centering" v-bind:class="{active: $route.name==='subscriptions/iot'}" tag="button" to="/subscriptions/iot">
							<span class="rigid icon-wrapper"><i class="fas fa-sync-alt"></i></span>
							<span class="rigid">Subscription</span>
						</router-link>

						<router-link class="tab row centering" v-bind:class="{active: $route.name==='payments'}" tag="button" to="/payments">
							<span class="rigid icon-wrapper"><i class="fas fa-credit-card"></i></span>
							<span class="rigid">Payments</span>
						</router-link>
					</menu>
				</header>
				<!-- </transition> -->

				<!-- <transition name="slide-left"> -->
					<router-view class="fill"></router-view>
				<!-- </transition> -->
			</div>

			<footer v-if="$root.isTek" class="column bottom rigid">
				<small class="centered"> Read about our <a target="_blank" href="https://www.tek.com/privacy-statement">privacy policy</a>. Copyright © Tektronix.</small>
			</footer>
		</main>




	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import DropdownMenu from './components/DropdownMenu';
import { handleTab } from './directives/handle-tab';
import { osTheme } from './directives/os-theme';
import ENV_VARS from '@/modules/env-vars.js';

export default {
	name: 'App',
	components: {
		DropdownMenu
	},
	directives: {
		handleTab,
		osTheme
	},
	data() {
		return {
			prevPath: null
		};
	},
	computed: {
		...mapGetters({
			'isAuthenticated' : 'auth/isAuthenticated',
			'referrerClientUrl' : 'auth/referrerClientUrl',
			'iotAppUrl' : 'auth/iotAppUrl',
			'signInUrl' : 'auth/signInUrl',
			'appSettingsUrl' : 'auth/appSettingsUrl',
			'appContactsUrl' : 'auth/appContactsUrl',
			'userEmail' : 'auth/userEmail',
			// remove once sub mgmt goes to app
			'helpUrl' : 'auth/scopeHelpUrl',
			'packagesUrl' : 'auth/scopePackagesUrl',
			'updateLicenseUrl' : 'auth/updateLicenseUrl',
			'installTekScopeUrl' : 'auth/installTekScopeUrl',
			'driveUrl' : 'auth/driveAppUrl',
			'scopeUrl' : 'auth/scopeAppUrl'
		}),
		...mapGetters([
			'user',
			'userStatus',
		]),
		...mapGetters({
			'hasLegacyTekScopeWithNoNewPromo': 'subscription/hasLegacyTekScopeWithNoNewPromo',
		}),
		getAppUrl() {
			//TODO rethink app specific controls
			return (ENV_VARS.BRAND_ID === 'initialstate') ? this.iotAppUrl : '#/';
		},
		hasDriveClaims(){
			return this.user && this.user.roles && this.user.roles.drive;
		},
		hasScopeClaims(){
			return this.user && this.user.roles && this.user.roles.tron;
		},
		hideMainHeader() {
			return this.$route.meta.hideMainHeader;
		},
		noScroll() {
			return this.$route.meta.noScroll;
		},
		pageNotFound() {
			return this.$route.name === 'unknown';
		},
		confirmEmailChange() {
			return this.$route.name === 'cea';
		},
		receipt() {
			return this.$route.name === 'receipt';
		},
		showValidationWarning(){
			return !this.user.emailVerified && this.userStatus.success;//remove isDev flag when releasing to prod
		},
		showLegacyBanner () {
			return this.hasLegacyTekScopeWithNoNewPromo && this.$route.name !== 'legacy-tekscope';
		}
	},
	created() {
		if (this.isAuthenticated) {
			this.$store.dispatch('USER_REQUEST');
		}
	}
};
</script>

<style src="./styles/app.styl"></style>



// WEBPACK FOOTER //
// ./src/App.vue